import type { SignupState } from "@gf/shared/signup";
import { Button, Callout, Field } from "@gf/ui";
import { useState, type FormEvent } from "react";
import { trackSignupStep } from "../../../lib/analytics";
import { MIN_PASSWORD } from "../../../components/accounts/shared";
import { PasswordInput } from "../PasswordInput";
import { signupApi } from "./api";
import { errorText, signupFailure } from "./flow";

/** Step 3: choose the password they'll sign in with (portal and extension). */
export function PasswordStep({
  session,
  email,
  onSaved,
  onExpired,
}: {
  session: string;
  email: string;
  onSaved: (state: SignupState) => void;
  onExpired: () => void;
}) {
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tooShort = password.length < MIN_PASSWORD;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    setBusy(true);
    setError(null);
    try {
      const state = await signupApi.password(session, password);
      trackSignupStep("password_saved");
      onSaved(state);
    } catch (err) {
      if (signupFailure(err) === "expired") return onExpired();
      setError(errorText(err, "We couldn't save your password. Try again."));
      setBusy(false);
    }
  }

  return (
    <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
      {/* Lets password managers save the login under the right email. */}
      <input className="signup-hp" type="email" name="username" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" />
      <Field
        label="Password"
        hint={`At least ${MIN_PASSWORD} characters.`}
        error={touched && tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
      >
        <PasswordInput autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} size="lg" autoFocus />
      </Field>
      {error ? <Callout tone="danger">{error}</Callout> : null}
      <Button type="submit" variant="primary" size="lg" block loading={busy}>
        Save password and continue
      </Button>
    </form>
  );
}
